<script setup lang="ts">
import IntlTelInput from "intl-tel-input/vueWithUtils";
import "intl-tel-input/styles";
import * as tel_i18n from "intl-tel-input/i18n";
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';

const { t, locale } = useI18n();
const site = useSiteData();
const { localized } = useLocaleValue();
const { phoneDisplay, phoneWhatsapp } = usePhoneHelpers();
const openingHours = computed(() => localized(site.opening_hours.labels) as { time: string; days: string });
const runtimeConfig = useRuntimeConfig();
const web3formsKey = runtimeConfig.public.web3formsKey as string | undefined;
const web3formsHcaptchaKey = runtimeConfig.public.web3formsHcaptchaKey as string | undefined;

const name = ref<string>("");
const email = ref<string>("");
const phone = ref<string>("");
const phone_valid = ref<boolean>(false);
const phone_error_code = ref<string>("");
const subject = ref<string>("");
const message = ref<string>("");

const formError = ref<string>("");
const submitSuccess = ref(false);
const isSubmitting = ref(false);

const mailSubject = computed(
  () => `${t("contact.form.subject_prefix")} ${localized(site.website_name)}`,
);

const can_submit = computed(() => {
  if (!name.value.trim()) return false;
  if (!email.value.trim()) return false;
  if (!phone_valid.value) return false;
  if (!subject.value.trim()) return false;
  if (!message.value.trim()) return false;
  return true;
});

function scrollToElement(id: string) {
  nextTick(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

function showError(msg: string) {
  formError.value = msg;
  scrollToElement("form-status");
}

function resetFormFields() {
  name.value = "";
  email.value = "";
  phone.value = "";
  phone_valid.value = false;
  phone_error_code.value = "";
  subject.value = "";
  message.value = "";
}

async function onSubmit() {
  if (!can_submit.value || isSubmitting.value) return;

  if (!phone_valid.value) {
    showError(t("contact.js.invalid_phone"));
    return;
  }

  const hCaptchaResponse = (
    document.querySelector<HTMLInputElement>('[name="h-captcha-response"]')?.value ?? ""
  ).trim();
  if (!hCaptchaResponse) {
    showError(t("contact.js.captcha_required"));
    return;
  }

  formError.value = "";

  if (!web3formsKey) {
    showError(t("contact.js.generic_error"));
    return;
  }

  isSubmitting.value = true;

  try {
    const formData = new FormData();
    formData.append("access_key", web3formsKey);
    formData.append("subject", mailSubject.value);
    formData.append("name", name.value.trim());
    formData.append("email", email.value.trim());
    formData.append("phone", phone.value.trim());
    formData.append("form_subject", subject.value.trim());
    formData.append("message", message.value.trim());
    formData.append("h-captcha-response", hCaptchaResponse);

    debugger;
    const response = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      body: formData,
    });

    const result = await response.json();

    if (result.success) {
      if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
        (window as any).gtag("event", "contact_form_submit", {
          event_category: "contact",
          event_label: "contact_form",
        });
      }
      submitSuccess.value = true;
      resetFormFields();
      scrollToElement("success-message");
    } else {
      showError(result.message || t("contact.js.generic_error"));
    }
  } catch {
    showError(t("contact.js.network_error"));
  } finally {
    isSubmitting.value = false;
  }
}

definePageMeta({
  page_name: "contact",
});

useHead({
  script: [
    {
      src: "https://web3forms.com/client/script.js",
      async: true,
      defer: true,
    },
  ],
});
</script>

<template>
  <section class="page-header">
    <div class="container">
      <h1>{{ t("contact.header.title") }}</h1>
      <p>
        {{ t("contact.header.subtitle") }}
        {{ localized(site.website_name) }}.
      </p>
    </div>
  </section>

  <section class="contact-content">
    <div class="container">
      <div class="contact-wrapper">
        <div class="contact-info">
          <h2>{{ t("contact.info.title") }}</h2>
          <p>{{ t("contact.info.subtitle") }}</p>

          <div class="contact-details">
            <div class="contact-item">
              <div class="contact-icon">
                <i class="fas fa-map-marker-alt"></i>
              </div>
              <div class="contact-text">
                <h3>{{ t("contact.details.location_label") }}</h3>
                <p>
                  <a :href="localized(site.maps_url)" target="_blank" rel="noopener noreferrer">
                    {{ t("contact.details.location_value") }}
                  </a>
                </p>
              </div>
            </div>

            <div class="contact-item">
              <div class="contact-icon">
                <i class="fas fa-envelope"></i>
              </div>
              <div class="contact-text">
                <h3>{{ t("contact.details.email_label") }}</h3>
                <p>
                  <a :href="`mailto:${localized(site.sales_email)}`">
                    {{ localized(site.sales_email) }}
                  </a>
                </p>
              </div>
            </div>

            <div
              v-for="(phone, index) in site.phone"
              :key="phone"
              class="contact-item"
            >
              <div class="contact-icon">
                <i class="fas fa-phone"></i>
              </div>
              <div class="contact-text">
                <h3>
                  {{ t("contact.details.phone_label") }}
                  {{ index + 1 }}
                </h3>
                <p>
                  <a :href="`tel:${localized(phone)}`" dir="ltr">
                    {{ phoneDisplay(localized(phone)) }}
                  </a>
                </p>
              </div>
            </div>

            <div class="contact-item">
              <div class="contact-icon">
                <i class="fab fa-whatsapp"></i>
              </div>
              <div class="contact-text">
                <h3>{{ t("contact.details.whatsapp_label") }}</h3>
                <p>
                  <a
                    :href="phoneWhatsapp(localized(site.phone[0]))"
                    target="_blank"
                    rel="noopener noreferrer"
                    dir="ltr"
                  >
                    {{ phoneDisplay(localized(site.phone[0])) }}
                  </a>
                </p>
              </div>
            </div>

            <div class="contact-item">
              <div class="contact-icon">
                <i class="fas fa-clock"></i>
              </div>
              <div class="contact-text">
                <h3>{{ t("contact.details.business_hours_label") }}</h3>
                <p>
                  {{ openingHours.time }}<br />
                  {{ openingHours.days }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="contact-form-wrapper">
          <div
            v-show="submitSuccess"
            id="success-message"
            class="form-success-message"
          >
            <div class="success-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h3>{{ t("contact.success.title") }}</h3>
            <p>{{ t("contact.success.message") }}</p>
          </div>

          <h2 v-show="!submitSuccess" id="form-title">
            {{ t("contact.form.title") }}
          </h2>

          <form
            v-show="!submitSuccess"
            id="contact-form"
            class="contact-form"
            name="contact-form"
            autocomplete="on"
            @submit.prevent="onSubmit"
          >
            <div class="form-group">
              <label for="name">{{ t("contact.form.labels.name") }} *</label>
              <input
                v-model="name"
                id="name"
                name="name"
                type="text"
                autocomplete="name"
                required
              />
            </div>

            <div class="form-group">
              <label for="email">{{ t("contact.form.labels.email") }} *</label>
              <input
                v-model="email"
                id="email"
                name="email"
                type="email"
                autocomplete="email"
                required
              />
            </div>

            <div class="form-group">
              <label for="phone">{{ t("contact.form.labels.phone") }} *</label>
              <ClientOnly>
                <IntlTelInput
                  :inputProps="{id: 'phone'}"
                  @changeNumber="phone = $event"
                  @changeValidity="phone_valid = $event"
                  @changeErrorCode="phone_error_code = $event"
                  :options="{
                    initialCountry: 'sa',
                    separateDialCode: true,
                    strictMode: true,
                    countryNameLocale: locale,
                    i18n: tel_i18n[locale],
                  }"
                />
              </ClientOnly>
            </div>

            <div class="form-group">
              <label for="subject">{{ t("contact.form.labels.subject") }} *</label>
              <input v-model="subject" id="subject" name="form_subject" type="text" required />
            </div>

            <div class="form-group">
              <label for="message">{{ t("contact.form.labels.message") }} *</label>
              <textarea v-model="message" id="message" name="message" rows="6" required />
            </div>

            <div class="form-group">
              <ClientOnly>
                <VueHcaptcha
                  :sitekey="web3formsHcaptchaKey"
                />
              </ClientOnly>
            </div>

            <div
              v-show="formError"
              id="form-status"
              class="form-status error"
              role="alert"
            >
              {{ formError }}
            </div>

            <button
              id="submit-btn"
              class="btn btn-primary"
              type="submit"
              :disabled="!can_submit || isSubmitting"
            >
              <span v-show="!isSubmitting" class="btn-text">
                {{ t("contact.form.submit.text") }}
              </span>
              <span v-show="isSubmitting" class="btn-loading">
                <i class="fas fa-spinner fa-spin"></i>
                {{ t("contact.form.submit.loading") }}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

